import { type SubmitHandler, useFormContext, useWatch } from 'react-hook-form';
import Button from '@/components/UI/Button';
import { HookForm } from '@/components/UI/HookForm/Form';
import { HookFileInput } from '@/components/UI/HookForm/Form/File';
import { HookInput } from '@/components/UI/HookForm/Form/Input';
import { HookReactSingleSelect } from '@/components/UI/HookForm/Form/ReactSelect';
import { RecentUploads } from '@/components/UI/HookForm/Form/RecentUploads';
import { HookTextarea } from '@/components/UI/HookForm/Form/TextArea';
import { useProjectPhases } from '@/hooks/ReactQueryHooks/useProjectQuery';
import { ChangeInDaysInput } from './ChangeInDaysInput';
import { MilestoneSelect } from './MilestoneSelect';
import type { MilestoneChangeOrderSchema } from './validationSchema';

interface MilestoneHookFormProps {
  projectId: string;
  title: string;
  onSubmit: SubmitHandler<MilestoneChangeOrderSchema>;
  isLoading?: boolean;
  hide: () => void;
  disableMilestone?: boolean;
}

const MilestoneHookForm = ({ hide, title, onSubmit, projectId, isLoading, disableMilestone }: MilestoneHookFormProps) => {
  const formMethods = useFormContext<MilestoneChangeOrderSchema>();
  const recentFiles = useWatch({
    control: formMethods.control,
    name: 'recentFiles',
  });

  const onRemoveRecentFile = (index: number) => {
    if (!recentFiles) return;

    const newValue = [...recentFiles];
    newValue.splice(index, 1);
    formMethods.setValue('recentFiles', newValue);
  };

  const phases = useProjectPhases();
  const phaseOptions = phases.data?.data.map((phase) => ({
    label: phase.name,
    value: phase.slug,
  }));

  return (
    <HookForm onSubmit={onSubmit}>
      <h2 className='mt-0 capitalize p-6 pb-4 mb-0 border-b border-gray-300'>{title}</h2>
      <section className='grid grid-cols-2 gap-5 items-center overflow-y-auto max-h-[70dvh] p-6 pt-4'>
        <HookReactSingleSelect
          formMethods={formMethods}
          label='Phase'
          options={phaseOptions}
          placeholder=''
          name='phase'
          isDisabled={disableMilestone}
        />
        <MilestoneSelect projectId={projectId} isDisabled={disableMilestone} />
        <HookInput disabled formMethods={formMethods} label='Due Date' type='date' name='dueDate' />
        <HookInput formMethods={formMethods} label='Proposed Due Date' type='date' name='proposedDueDate' />
        <ChangeInDaysInput />
        <HookTextarea formMethods={formMethods} wrapperClassName='col-span-2' label='Why is this Change Required?' name='reason' rows={3} />
        <div className='col-span-2'>
          <HookFileInput
            formMethods={formMethods}
            label='Template Documents'
            name='templateDocuments'
            multiple
            customClassName='overflow-hidden'
          />
          <RecentUploads recentFiles={recentFiles} onRemove={onRemoveRecentFile} />
        </div>
        <HookInput formMethods={formMethods} customClassName='col-span-2' label='Response Due By' type='date' name='responseDueBy' />
        <HookTextarea
          formMethods={formMethods}
          wrapperClassName='col-span-2'
          label='No Response Consequence'
          name='noResponseConsequence'
          rows={3}
        />
        <HookTextarea formMethods={formMethods} wrapperClassName='col-span-2' label='Notes' name='notes' rows={3} />
      </section>

      <div className='flex gap-2 justify-end p-6 border-t border-gray-300'>
        <Button label='Cancel' btnStyle='neutral' onClick={hide} />
        <Button label={isLoading ? 'Loading...' : 'Apply'} btnStyle='primary' btnType='submit' />
      </div>
    </HookForm>
  );
};

export default MilestoneHookForm;
